import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import {
  Sparkles, Save, Loader2, ChevronDown, Info, Book,
  FolderOpen, MessageSquare, Table as TableIcon,
} from "lucide-react";

const CATEGORIES = ["Food", "Beverage", "Beauty", "Personal Care", "Home",
  "Supplements", "Pet", "Other"];
const STAGES = ["Idea", "Formulation", "Pre-launch", "Selling online",
  "In retail", "Scaling"];

// PersonalAiContextPanel — mounted on /account. Lets the member see what
// the ElevenLabs voice agent (and Warren AI chat) will know about them
// and edit the parts that are theirs to control:
//   • Brand profile (mirrored from `users.directory`)
//   • Free-form personal AI note
// Derived data (progress, projects, recent Q&A) is shown read-only so
// the member always knows what's being shared with the agent.
export default function PersonalAiContextPanel() {
  const [ctx, setCtx] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({ progress: false, projects: false, qna: false });

  const load = () =>
    api.get("/kb/me").then((r) => {
      setCtx(r.data);
      setForm({
        brand:            r.data.brand_profile?.brand || "",
        category:         r.data.brand_profile?.category || "",
        stage:            r.data.brand_profile?.stage || "",
        city:             r.data.brand_profile?.city || "",
        bio:              r.data.brand_profile?.bio || "",
        website:          r.data.brand_profile?.website || "",
        instagram:        r.data.brand_profile?.instagram || "",
        linkedin:         r.data.brand_profile?.linkedin || "",
        personal_ai_note: r.data.personal_ai_note || "",
      });
    }).catch((e) => toast.error(formatError(e)));

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.put("/kb/me/preferences", form);
      setCtx(r.data);
      toast.success("Personal AI context saved");
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const derivedSummary = useMemo(() => {
    if (!ctx) return null;
    const p = ctx.progress || {};
    const pr = ctx.projects || {};
    return {
      completedModules: p.completed_modules_count || 0,
      totalModules:     p.total_modules || 0,
      foldersCount:     (pr.folders || []).length,
      itemsCount:       (pr.items || []).length,
      savedListsCount:  (pr.saved_lists || []).length,
      qnaCount:         (ctx.recent_qna || []).length,
    };
  }, [ctx]);

  if (!ctx || !form) {
    return (
      <div className="surface p-6" data-testid="personal-ai-context-loading">
        <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Personal AI context
        </p>
        <div className="mt-4 text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading what the AI knows…
        </div>
      </div>
    );
  }

  return (
    <div className="surface p-6" data-testid="personal-ai-context">
      <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1">
        <Sparkles className="w-3 h-3" /> Personal AI context
      </p>
      <h2 className="font-display text-2xl mt-2">What the AI knows about you</h2>
      <p className="text-sm text-muted-foreground mt-1">
        Morgan (voice + chat) uses everything below to answer you personally.
        Edit the fields marked <span className="font-semibold text-foreground">editable</span> — the rest is
        derived from your progress and activity.
      </p>

      <form onSubmit={submit} className="mt-6 grid gap-6">
        {/* ── Brand profile — editable ─────────────────────────────── */}
        <section>
          <div className="flex items-baseline gap-2">
            <h3 className="font-display text-lg">Brand profile</h3>
            <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary">
              Editable
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Also visible in the Member Directory. Editing here syncs both places.
          </p>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <label className="grid gap-1">
              <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Brand name</span>
              <input value={form.brand} onChange={set("brand")}
                data-testid="pac-brand"
                placeholder="e.g. Kettle & Grain"
                className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40" />
            </label>
            <label className="grid gap-1">
              <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Category</span>
              <select value={form.category} onChange={set("category")}
                data-testid="pac-category"
                className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40 bg-white">
                <option value="">Select…</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="grid gap-1">
              <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Stage</span>
              <select value={form.stage} onChange={set("stage")}
                data-testid="pac-stage"
                className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40 bg-white">
                <option value="">Select…</option>
                {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="grid gap-1">
              <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">City</span>
              <input value={form.city} onChange={set("city")}
                data-testid="pac-city"
                placeholder="e.g. Austin, TX"
                className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40" />
            </label>
            <label className="grid gap-1 sm:col-span-2">
              <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Short bio</span>
              <textarea value={form.bio} onChange={set("bio")} rows={3}
                data-testid="pac-bio"
                placeholder="1-2 sentences the AI can quote when introducing your brand."
                className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40 resize-none" />
              <span className="text-[10px] text-muted-foreground text-right">{(form.bio || "").length}/400</span>
            </label>
            <label className="grid gap-1">
              <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Website</span>
              <input value={form.website} onChange={set("website")}
                data-testid="pac-website" placeholder="https://…"
                className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40" />
            </label>
            <label className="grid gap-1">
              <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Instagram</span>
              <input value={form.instagram} onChange={set("instagram")}
                data-testid="pac-instagram" placeholder="@yourbrand"
                className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40" />
            </label>
            <label className="grid gap-1 sm:col-span-2">
              <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">LinkedIn</span>
              <input value={form.linkedin} onChange={set("linkedin")}
                data-testid="pac-linkedin" placeholder="linkedin.com/in/…"
                className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40" />
            </label>
          </div>
        </section>

        {/* ── Personal AI note — editable ──────────────────────────── */}
        <section>
          <div className="flex items-baseline gap-2">
            <h3 className="font-display text-lg">Personal note to the AI</h3>
            <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary">
              Editable
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            A free-form note the AI reads before every conversation. Great for
            goals, constraints, taste in advice, or anything you want it to
            remember about you.
          </p>
          <textarea
            value={form.personal_ai_note}
            onChange={set("personal_ai_note")}
            data-testid="pac-personal-note"
            rows={5}
            placeholder='e.g. "I&apos;m bootstrapping a low-caffeine matcha brand for busy parents. Focus your answers on getting into Whole Foods Northeast in 12 months. Cite specific brokers and buyer contacts when you can."'
            className="mt-3 w-full rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40 resize-none"
          />
          <span className="text-[10px] text-muted-foreground text-right block">{(form.personal_ai_note || "").length}/2000</span>
        </section>

        <div className="flex items-center gap-3 flex-wrap">
          <button type="submit" disabled={saving} data-testid="pac-save"
            className="btn-primary text-sm disabled:opacity-60 inline-flex items-center gap-1.5">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {saving ? "Saving…" : "Save changes"}
          </button>
          <p className="text-xs text-muted-foreground inline-flex items-center gap-1">
            <Info className="w-3 h-3" /> Changes take effect the next time you start a voice or chat session.
          </p>
        </div>

        {/* ── Derived context — read-only ──────────────────────────── */}
        <section className="border-t border-shell-border pt-5">
          <div className="flex items-baseline gap-2">
            <h3 className="font-display text-lg">What else the AI sees</h3>
            <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-shell-border text-muted-foreground">
              Read-only
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Progress + projects + recent Q&amp;A are pulled live at conversation
            start. Nothing here is stored on our AI server — it's re-generated
            per request.
          </p>

          <div className="mt-4 grid sm:grid-cols-3 gap-3" data-testid="pac-derived-tiles">
            <DerivedTile
              icon={Book} label="Course progress"
              value={`${derivedSummary.completedModules} / ${derivedSummary.totalModules}`}
              detail="modules completed"
              expanded={expanded.progress}
              onToggle={() => setExpanded((e) => ({ ...e, progress: !e.progress }))}
              testid="pac-tile-progress"
            >
              {(ctx.progress?.completed_module_slugs || []).slice(0, 8).map((slug) => (
                <li key={slug} className="text-xs text-muted-foreground truncate">✓ {slug}</li>
              ))}
              {(ctx.progress?.completed_module_slugs || []).length === 0 && (
                <li className="text-xs text-muted-foreground italic">No completions yet</li>
              )}
            </DerivedTile>
            <DerivedTile
              icon={FolderOpen} label="Projects"
              value={`${derivedSummary.itemsCount}`}
              detail={`${derivedSummary.foldersCount} folders · ${derivedSummary.savedListsCount} saved lists`}
              expanded={expanded.projects}
              onToggle={() => setExpanded((e) => ({ ...e, projects: !e.projects }))}
              testid="pac-tile-projects"
            >
              {(ctx.projects?.items || []).slice(0, 6).map((p) => (
                <li key={p.id} className="text-xs text-muted-foreground truncate">
                  · {p.name}{p.module_slug ? ` (${p.module_slug})` : ""}
                </li>
              ))}
              {(ctx.projects?.saved_lists || []).slice(0, 4).map((l) => (
                <li key={l.id} className="text-xs text-muted-foreground truncate">
                  <TableIcon className="inline w-3 h-3 mr-1" />
                  {l.name} — {l.database_slug} ({(l.row_ids || []).length} rows)
                </li>
              ))}
              {derivedSummary.itemsCount + derivedSummary.savedListsCount === 0 && (
                <li className="text-xs text-muted-foreground italic">No saved projects yet</li>
              )}
            </DerivedTile>
            <DerivedTile
              icon={MessageSquare} label="Recent Q&A"
              value={`${derivedSummary.qnaCount}`}
              detail="last messages retained"
              expanded={expanded.qna}
              onToggle={() => setExpanded((e) => ({ ...e, qna: !e.qna }))}
              testid="pac-tile-qna"
            >
              {(ctx.recent_qna || []).slice(-5).map((m, i) => (
                <li key={i} className="text-xs text-muted-foreground line-clamp-2">
                  <span className={"font-semibold " + (m.role === "user" ? "text-foreground" : "text-primary")}>
                    {m.role === "user" ? "You" : "Morgan"}:
                  </span> {m.content}
                </li>
              ))}
              {(ctx.recent_qna || []).length === 0 && (
                <li className="text-xs text-muted-foreground italic">No Q&A history yet</li>
              )}
            </DerivedTile>
          </div>
        </section>
      </form>
    </div>
  );
}

function DerivedTile({ icon: Icon, label, value, detail, children, expanded, onToggle, testid }) {
  return (
    <div className="rounded-xl border border-shell-border bg-white p-4" data-testid={testid}>
      <button type="button" onClick={onToggle}
        className="w-full flex items-center justify-between text-left gap-2">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold inline-flex items-center gap-1">
            <Icon className="w-3 h-3" /> {label}
          </p>
          <p className="font-display text-xl mt-1">{value}</p>
          <p className="text-xs text-muted-foreground">{detail}</p>
        </div>
        <ChevronDown className={"w-4 h-4 text-muted-foreground transition-transform " + (expanded ? "rotate-180" : "")} />
      </button>
      {expanded && (
        <ul className="mt-3 grid gap-1 border-t border-shell-border pt-3">
          {children}
        </ul>
      )}
    </div>
  );
}
